import React, { useState } from "react";
import Papa from "papaparse";
import ModalView from "@modals/modalview/ModalView";
import fileIcon from "@assets/icons/file-upload.svg";
import incompleteIcon from "@assets/icons/incomplete.svg";
import completedIcon from "@assets/icons/completed.svg";
import styles from "./UploadEvaluatorDetails.module.css";
import { assignEvaluatorPools } from "@services/evaluationService";

interface UploadEvaluatorDetailsProps {
  isOpen: boolean;
  examSessionId: any;
  questionPaperId: any;
  examWorkflowStateId: any;
  handleSave: any;
  onClose: () => void;
}
function UploadEvaluatorDetails({
  isOpen,
  examSessionId,
  examWorkflowStateId,
  handleSave,
  onClose,
}: UploadEvaluatorDetailsProps) {
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "completed" | "error"
  >("idle");
  const [examSessionData, setExamSessionData] = useState<any>(null);
  const updateEvaluatorPoolData = async (payload: any) => {
    const res = await assignEvaluatorPools(
      payload,
      examSessionId,
      examWorkflowStateId
    );
    if (res) {
      setExamSessionData(res);
    }
  };
  const handleUploadEvaluatorData = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 20 * 1024 * 1024;
    const errors: string[] = [];

    if (file.size > MAX_FILE_SIZE) {
      errors.push(`File size exceeds 20 MB limit.`);
    }

    setUploadStatus("uploading");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data as {
          name: string;
          evaluators: string; 
          questions: string;
        }[];

        const payload = {
          evaluator_pools: rows.map((row) => ({
            name: row.name?.trim() || "",

            members: (row.evaluators || "")
              .split(",")
              .map((ev) => ev.trim())
              .filter((ev) => ev !== "")
              .map((ev) => ({
                evaluator_id: ev,
              })),

            questions: (row.questions || "")
              .split(",")
              .map((q) => q.trim())
              .filter((q) => q !== "" && !isNaN(Number(q)))
              .map((q) => ({
                sequence_number: Number(q),
              })),
          })),
        };

        console.log("FINAL PAYLOAD:", payload);
        try {
          await updateEvaluatorPoolData(payload);
          setUploadStatus("completed");
        } catch (error) {
          console.error("Error updating evaluator pool data:", error);
          setUploadStatus("error");
          return;
        }
      },
      error: (err) => {
        console.error("PapaParse Error:", err);
        setUploadStatus("error");
      },
    });
  };

  return (
    <ModalView isOpen={isOpen} onClose={onClose}>
      <div className={styles.studentListUploadContainer}>
        <div className={styles.header}>
          <h2>Assign evaluators</h2>
        </div>
        <div className={styles.content}>
          {uploadStatus === "idle" && (
            <div className={styles.fileUploadContainer}>
              <div className={styles.fileUpload} style={{ display: "flex" }}>
                <form className={styles.form}>
                  <input
                    type="file"
                    accept=".csv"
                    id="fileUpload"
                    className={styles.fileInput}
                    onChange={handleUploadEvaluatorData}
                  />
                  <label htmlFor="fileUpload" className={styles.fileLabel}>
                    UPLOAD FILE
                    <img src={fileIcon} alt="Upload" />
                  </label>
                </form>
                <div className={styles.fileInfo}>
                  <p>
                    Please{" "}
                    <a
                      href="src/assets/csvs/assignEvaluatorsTemplate.csv"
                      download
                    >
                      click here
                    </a>{" "}
                    to download a template for .csv file format
                  </p>
                  <p>File size must be less than 20MB</p>
                  <p>Supported format for upload - .csv</p>
                </div>
              </div>
            </div>
          )}
          {uploadStatus === "uploading" && (
            <div className={styles.fileUploading}>
              <p className={styles.uploadingText}>File Uploading...</p>
            </div>
          )}
          {uploadStatus === "completed" && (
            <div className={styles.fileUploaded}>
              <p className={styles.uploadingCompleted}>
                UPLOAD COMPLETED! <img src={completedIcon} alt="Completed" />
              </p>
            </div>
          )}
          {uploadStatus === "error" && (
            <div className={styles.fileUploadFailed}>
              <div className={styles.uploadingFailedContainer}>
                <p className={styles.uploadingFailed}>Oops! Upload Failed</p>
                <img src={incompleteIcon} alt="Incomplete" />
              </div>
              <p className={styles.errorMessage}></p>
            </div>
          )}
        </div>
        <div className={styles.buttonContainer}>
          <button
            className={styles.cancelButton}
            onClick={() => {
              setUploadStatus("idle");
              onClose();
            }}
          >
            Cancel
          </button>
          <button
            className={styles.continueButton}
            onClick={() => {
              setUploadStatus("idle");
              handleSave(examSessionData);
            }}
            disabled={uploadStatus !== "completed"}
          >
            Save
          </button>
        </div>
      </div>
    </ModalView>
  );
}

export default UploadEvaluatorDetails;
